import { useCallback, useEffect, useState } from 'react';
import { RechtsLinks } from '../../rechtliches/komponenten/RechtsRahmen';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import type { Angebot, Tarif as TarifTyp } from '../../../gemeinsam/lib/typen';
import { Abzeichen, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import SepaMandat from '../../bank/komponenten/SepaMandat';
import { AboStatus, Kuendigung, TarifWechsel } from '../komponenten/AboVerwaltung';
import Datenkopie from '../../mandanten/komponenten/Datenkopie';

const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const datum = (s: string) => new Date(s).toLocaleDateString('de-DE');

export default function Tarif() {
  const { t, sprache } = useSprache();
  const { aktualisieren, istAdmin } = useAuth();
  const [mandatAendern, setMandatAendern] = useState(false);
  const [tarif, setTarif] = useState<TarifTyp | null>(null);
  const [angebote, setAngebote] = useState<Angebot[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [nimmtAn, setNimmtAn] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      // Die Leistungsliste des Tarifs liegt auf dem Server — deshalb geht die
      // gewählte Sprache mit, sonst steht hier Deutsch in einer englischen App.
      const [t, a] = await Promise.all([
        api<TarifTyp>(`/billing/current?sprache=${sprache}`),
        api<Angebot[]>('/billing/offer/mine').catch(() => [] as Angebot[]),
      ]);
      setTarif(t);
      setAngebote(a);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Tarifdaten konnten nicht geladen werden.');
    }
  }, [sprache]);

  useEffect(() => { void laden(); }, [laden]);

  async function annehmen(angebot: Angebot) {
    setNimmtAn(angebot.id);
    setFehler(null);
    try {
      const ergebnis = await api<{ needsMandate: boolean; activeUntil: string | null }>(
        `/billing/offer/${angebot.id}/accept`, { methode: 'POST' });
      await laden();
      await aktualisieren(); // Feature-Flags in der Navigation sofort mitziehen
      setMeldung(
        `Angebot „${angebot.label}“ ist aktiv.`
        + (ergebnis.activeUntil ? ` Befristet bis ${datum(ergebnis.activeUntil)}.` : '')
        + (ergebnis.needsMandate ? ' Bitte hinterlegen Sie noch ein SEPA-Lastschriftmandat.' : ''),
      );
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Angebot konnte nicht angenommen werden.');
    } finally {
      setNimmtAn(null);
    }
  }

  if (!tarif) {
    return fehler
      ? <Hinweis art="fehler">{fehler}</Hinweis>
      : <div className="flex justify-center py-16"><Spinner /></div>;
  }

  const offen = angebote.filter(a => a.status === 'pending');
  const sonderpreis = tarif.customOffer && tarif.listPrice !== null && tarif.price !== tarif.listPrice;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('tarif.titel')}</h1>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />
      <AboStatus tarif={tarif} neuLaden={laden} />

      {offen.length > 0 && (
        <Karte klasse="border-marke-300 dark:border-marke-800">
          <KartenKopf titel={t('tarif.angebot')} hinweis="Vom Anbieter für Sie hinterlegt" />
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {offen.map(a => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 dark:text-slate-100">{a.label}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {euro(a.monthlyPrice)} pro Monat
                    {a.durationMonths ? ` · ${a.durationMonths} Monate Laufzeit` : ' · unbefristet'}
                  </p>
                  {a.note && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{a.note}</p>}
                </div>
                <Knopf variante="primaer" laedt={nimmtAn === a.id} onClick={() => annehmen(a)}>
                  Angebot annehmen
                </Knopf>
              </li>
            ))}
          </ul>
        </Karte>
      )}

      <Karte>
        <KartenKopf
          titel={t('tarif.aktuell')}
          aktion={tarif.customOffer ? <Abzeichen art="blau">{t('tarif.sonderkondition')}</Abzeichen> : undefined}
        />
        <div className="p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{tarif.planLabel}</span>
            {tarif.price !== null && (
              <span className="text-lg text-slate-700 dark:text-slate-300">
                {euro(tarif.price)}<span className="text-sm text-slate-500 dark:text-slate-400"> {t('start.preise.proMonat')}</span>
              </span>
            )}
            {/* Sonderpreis: Listenpreis zum Vergleich durchgestrichen daneben. */}
            {sonderpreis && tarif.listPrice !== null && (
              <span className="text-sm text-slate-400 line-through dark:text-slate-500">{euro(tarif.listPrice)}</span>
            )}
          </div>
          {tarif.planDescription && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{tarif.planDescription}</p>
          )}
          {tarif.customOffer?.activeUntil && (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
              Sonderkondition befristet bis {datum(tarif.customOffer.activeUntil)} — danach gilt wieder der Listenpreis.
            </p>
          )}

          <div className="mt-5 grid gap-6 sm:grid-cols-2">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('tarif.enthalten')}</h3>
              <ul className="space-y-1">
                {tarif.features.map(f => (
                  <li key={f} className="flex gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="text-emerald-600 dark:text-emerald-400" aria-hidden>✓</span>{f}
                  </li>
                ))}
              </ul>
            </div>
            {tarif.notIncluded.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('tarif.nichtEnthalten')}</h3>
                <ul className="space-y-1">
                  {tarif.notIncluded.map(f => (
                    <li key={f} className="flex gap-2 text-sm text-slate-500 dark:text-slate-400">
                      <span aria-hidden>—</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Karte>

      <TarifWechsel tarif={tarif} neuLaden={laden} />

      <Karte>
        <KartenKopf titel={t('tarif.mandat')}
                    aktion={istAdmin && tarif.sepa && !mandatAendern
                      ? <Knopf variante="still" className="!text-xs" onClick={() => setMandatAendern(true)}>{t('abo.mandatAendern')}</Knopf>
                      : undefined} />
        <div className="space-y-3 p-5 text-sm">
          {tarif.sepa && (
            <div className="text-slate-700 dark:text-slate-300">
              <p>{tarif.sepa.holder}</p>
              <p className="font-mono text-slate-500 dark:text-slate-400">{tarif.sepa.ibanMasked}</p>
              <p className="mt-1 text-slate-500 dark:text-slate-400">Erteilt am {datum(tarif.sepa.signedAt)}</p>
            </div>
          )}
          {!tarif.sepa && !tarif.customOffer && tarif.abo.phase !== 'gekuendigt' && (
            <Hinweis art="warnung">
              Es ist kein SEPA-Lastschriftmandat hinterlegt. Ohne Mandat kann der Beitrag nicht eingezogen werden.
            </Hinweis>
          )}
          {/* Erteilen oder ändern — ein Tarifwechsel fragt die Bankverbindung nicht mehr ab. */}
          {istAdmin && (!tarif.sepa || mandatAendern) && tarif.abo.phase !== 'beendet' && tarif.abo.phase !== 'gekuendigt' && (
            <SepaMandat kompakt beiErteilt={async () => { setMandatAendern(false); await laden(); await aktualisieren(); }} />
          )}
        </div>
      </Karte>

      <Kuendigung tarif={tarif} neuLaden={laden} />
      {tarif.abo.phase === 'gekuendigt' && <Datenkopie />}
      <RechtsLinks />

      {tarif.history.length > 0 && (
        <Karte>
          <KartenKopf titel={t('tarif.verlauf')} />
          <ul className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
            {tarif.history.map(h => (
              <li key={h.id} className="flex items-center justify-between px-5 py-3">
                <span className="text-slate-700 dark:text-slate-300">
                  {h.fromPlan} → {h.toPlan}
                  {h.source === 'custom_offer' && <span className="ml-2 text-slate-500 dark:text-slate-400">({t('tarif.sonderkondition')})</span>}
                </span>
                <span className="text-slate-500 dark:text-slate-400">{datum(h.changedAt)}</span>
              </li>
            ))}
          </ul>
        </Karte>
      )}
    </div>
  );
}
